import { useState, useEffect, useRef, useMemo } from "react";
import { requestGoogleCalendarSync, requestPortalSync } from '@/services/googleCalendarSync';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, MapPin, Users, AlertCircle, Plus, Search, Bell, Wallet } from "lucide-react";
import { formatBRLFromDigits, numberToBRLInputFormat, parseBRLStringToNumber } from "@/utils/brlFormat";
import { Switch } from "@/components/ui/switch";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface CreateAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date;
  onAppointmentCreated: () => void;
  initialData?: any; // Dados iniciais para edição
}

interface Case {
  id: string;
  title: string;
  case_number?: string;
  case_number_cnj?: string;
  client_id?: string;
  clients?: { id: string; name: string };
}

interface Profile {
  id: string;
  full_name: string;
}

interface ClientOption {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
}

// Busca sem diferenciar acentos/maiúsculas; números comparados só pelos dígitos
const normText = (v?: string | null) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const onlyDigits = (v?: string | null) => String(v || '').replace(/\D/g, '');

const FEE_CATEGORY: Record<string, string> = {
  diligencia: 'Diligência',
  audiencia: 'Audiência',
  consulta: 'Consulta',
};

const appointmentTypes = [
  { value: "audiencia", label: "Audiência", color: "#22c55e" }, // Green
  { value: "prazo", label: "Prazo", color: "#eab308" }, // Yellow
  { value: "atendimento", label: "Atendimento", color: "#3b82f6" }, // Blue
  { value: "outro", label: "Outro", color: "#6b7280" }, // Gray
  { value: "consulta", label: "Consulta", color: "#3b82f6" },
  { value: "reuniao", label: "Reunião", color: "#059669" },
  { value: "depoimento", label: "Depoimento", color: "#7c3aed" },
  { value: "pericia", label: "Perícia", color: "#ea580c" },
  { value: "mediacao", label: "Mediação", color: "#0891b2" },
  { value: "conciliacao", label: "Conciliação", color: "#65a30d" },
  { value: "diligencia", label: "Diligência", color: "#be185d" },
  { value: "inspacao_judicial", label: "Inspeção Judicial", color: "#374151" },
  { value: "sessao_julgamento", label: "Sessão de Julgamento", color: "#1f2937" }
];

const priorityLevels = [
  { value: "baixa", label: "Baixa", color: "bg-green-500" },
  { value: "normal", label: "Normal", color: "bg-blue-500" },
  { value: "alta", label: "Alta", color: "bg-yellow-500" },
  { value: "urgente", label: "Urgente", color: "bg-orange-500" },
  { value: "critica", label: "Crítica", color: "bg-red-500" }
];

// Mesmos presets do CreateTaskModal — UX consistente entre tarefas e atendimentos.
const REMINDER_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "Na hora" },
  { value: 10, label: "10 min antes" },
  { value: 30, label: "30 min antes" },
  { value: 60, label: "1 h antes" },
  { value: 120, label: "2 h antes" },
  { value: 1440, label: "1 dia antes" },
  { value: 2880, label: "2 dias antes" },
];

export const CreateAppointmentModal = ({ 
  isOpen, 
  onClose, 
  selectedDate, 
  onAppointmentCreated, 
  initialData 
}: CreateAppointmentModalProps) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    appointment_date: format(selectedDate, "yyyy-MM-dd"),
    appointment_time: "09:00",
    duration: 60,
    appointment_type: "consulta",
    priority: "normal",
    location: "",
    prospect_name: "", // This will now be used for client name if no case is linked
    prospect_email: "",
    prospect_phone: "",
    video_conference_link: "",
    is_billable: false,
    fee_amount: "",
    fee_to_financial: true,
    reminder_time: 60,
    push_notify: true,
    reminder_offsets: [0, 60] as number[],
    case_id: null as string | null,
    client_id: null as string | null,
    responsible_id: null as string | null,
    show_in_portal: false,
  });

  const { toast } = useToast();

  // State for Case Autocomplete
  const [caseSearchTerm, setCaseSearchTerm] = useState("");
  const [allCases, setAllCases] = useState<Case[]>([]);
  const [allClients, setAllClients] = useState<ClientOption[]>([]);
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [clientPopoverOpen, setClientPopoverOpen] = useState(false);
  const filteredCases = useMemo(() => {
    const term = normText(caseSearchTerm);
    const digits = onlyDigits(caseSearchTerm);
    const list = !term
      ? allCases
      : allCases.filter((c) =>
          normText(c.title).includes(term) ||
          normText(c.clients?.name).includes(term) ||
          (digits.length >= 3 && (onlyDigits(c.case_number_cnj).includes(digits) || onlyDigits(c.case_number).includes(digits)))
        );
    return list.slice(0, 50);
  }, [allCases, caseSearchTerm]);
  const filteredClients = useMemo(() => {
    const term = normText(clientSearchTerm);
    return (term ? allClients.filter((c) => normText(c.name).includes(term)) : allClients).slice(0, 50);
  }, [allClients, clientSearchTerm]);
  const setFilteredCases = (_: Case[]) => {};
  const [casePopoverOpen, setCasePopoverOpen] = useState(false);
  const caseInputRef = useRef<HTMLInputElement>(null);

  // State for Responsible Autocomplete
  const [responsibleSearchTerm, setResponsibleSearchTerm] = useState("");
  const [filteredResponsibles, setFilteredResponsibles] = useState<Profile[]>([]);
  const [responsiblePopoverOpen, setResponsiblePopoverOpen] = useState(false);
  const responsibleInputRef = useRef<HTMLInputElement>(null);

  // Processos e clientes para os seletores (carregados ao abrir a janela)
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      const [casesRes, clientsRes] = await Promise.all([
        supabase.from('cases').select('id, title, case_number, case_number_cnj, client_id, archived, clients:client_id(id, name)').order('title'),
        supabase.from('clients').select('id, name, email, phone, mobile').order('name'),
      ]);
      if (casesRes.error) console.error('Erro ao carregar processos:', casesRes.error);
      setAllCases(((casesRes.data || []) as any[]).filter((c) => !c.archived) as Case[]);
      setAllClients((clientsRes.data || []) as ClientOption[]);
    })();
  }, [isOpen]);

  // Fetch responsibles for autocomplete
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (responsibleSearchTerm.length > 2) {
        searchResponsibles(responsibleSearchTerm);
      } else {
        setFilteredResponsibles([]);
      }
    }, 300); // Debounce for 300ms

    return () => clearTimeout(delayDebounceFn);
  }, [responsibleSearchTerm]);

  // Initialize form data when editing
  useEffect(() => {
    if (initialData && isOpen) {
      const appointmentDate = new Date(initialData.appointment_date);
      const timeString = appointmentDate.toTimeString().slice(0, 5); // Get HH:mm format
      
      setFormData({
        title: initialData.title || "",
        description: initialData.description || "",
        appointment_date: format(appointmentDate, "yyyy-MM-dd"),
        appointment_time: timeString,
        duration: initialData.duration || 60,
        appointment_type: initialData.appointment_type || "consulta",
        priority: initialData.priority || "normal",
        location: initialData.location || "",
        prospect_name: initialData.prospect_name || "",
        prospect_email: initialData.prospect_email || "",
        prospect_phone: initialData.prospect_phone || "",
        video_conference_link: initialData.video_conference_link || "",
        is_billable: initialData.is_billable || false,
        fee_amount: initialData.estimated_cost ? numberToBRLInputFormat(Number(initialData.estimated_cost)) : "",
        fee_to_financial: true,
        reminder_time: initialData.reminder_time || 60,
        push_notify: (Array.isArray(initialData.reminder_offsets_minutes) && initialData.reminder_offsets_minutes.length > 0)
          || initialData.reminder_time !== 0,
        reminder_offsets: Array.isArray(initialData.reminder_offsets_minutes) && initialData.reminder_offsets_minutes.length > 0
          ? initialData.reminder_offsets_minutes
          : [0, 60],
        case_id: initialData.case_id || null,
        client_id: initialData.client_id || null,
        responsible_id: initialData.responsible_id || null,
        show_in_portal: !!initialData.show_in_portal,
      });

      // Set search terms for linked data
      if (initialData.cases?.title) {
        setCaseSearchTerm(initialData.cases.title);
      }
      if (initialData.clients?.name) {
        setCaseSearchTerm(initialData.clients.name);
      }
    } else if (!initialData && isOpen) {
      // Reset form when creating new appointment
      setFormData({
        title: "",
        description: "",
        appointment_date: format(selectedDate, "yyyy-MM-dd"),
        appointment_time: "09:00",
        duration: 60,
        appointment_type: "consulta",
        priority: "normal",
        location: "",
        prospect_name: "",
        prospect_email: "",
        prospect_phone: "",
        video_conference_link: "",
        is_billable: false,
    fee_amount: "",
    fee_to_financial: true,
        reminder_time: 60,
        push_notify: true,
        reminder_offsets: [0, 60],
        case_id: null,
        client_id: null,
        responsible_id: null,
        show_in_portal: false,
      });
      setCaseSearchTerm("");
      setResponsibleSearchTerm("");
    }
  }, [initialData, isOpen, selectedDate]);

  const toggleReminderOffset = (value: number) => {
    setFormData((prev) => {
      const exists = prev.reminder_offsets.includes(value);
      const next = exists
        ? prev.reminder_offsets.filter((v) => v !== value)
        : [...prev.reminder_offsets, value].sort((a, b) => a - b);
      return { ...prev, reminder_offsets: next };
    });
  };

  const searchCases = async (term: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('cases')
        .select(`id, title, case_number, case_number_cnj, clients(id, name)`)
        .or(`title.ilike.%${term}%,case_number.ilike.%${term}%,case_number_cnj.ilike.%${term}%`)
        .limit(10);

      if (error) throw error;
      setFilteredCases(data || []);
    } catch (error: any) {
      console.error("Erro ao buscar processos:", error);
      toast({
        title: "Erro",
        description: "Não foi possível buscar processos para vincular.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const searchResponsibles = async (term: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles') // Assuming 'profiles' table for users/lawyers
        .select(`id, full_name`)
        .ilike('full_name', `%${term}%`)
        .limit(10);

      if (error) throw error;
      setFilteredResponsibles(data || []);
    } catch (error: any) {
      console.error("Erro ao buscar responsáveis:", error);
      toast({
        title: "Erro",
        description: "Não foi possível buscar responsáveis.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCaseSelect = (caseItem: Case) => {
    const client = allClients.find((c) => c.id === (caseItem.clients?.id || caseItem.client_id));
    setFormData(prev => ({
      ...prev,
      case_id: caseItem.id,
      client_id: client?.id || caseItem.clients?.id || null,
      prospect_name: client?.name || caseItem.clients?.name || prev.prospect_name,
      prospect_email: client?.email || prev.prospect_email,
      prospect_phone: client?.mobile || client?.phone || prev.prospect_phone,
    }));
    setCaseSearchTerm(caseItem.title || caseItem.case_number || caseItem.case_number_cnj || "");
    setCasePopoverOpen(false);
  };

  const handleClientSelect = (client: ClientOption | null) => {
    setFormData(prev => ({
      ...prev,
      client_id: client?.id || null,
      prospect_name: client?.name || '',
      prospect_email: client?.email || '',
      prospect_phone: client?.mobile || client?.phone || '',
      // Trocou o cliente: desvincula processo de outro cliente
      case_id: client && prev.case_id && allCases.find((c) => c.id === prev.case_id)?.clients?.id !== client.id ? null : prev.case_id,
    }));
    setClientSearchTerm('');
    setClientPopoverOpen(false);
  };

  const handleResponsibleSelect = (responsible: Profile) => {
    setFormData(prev => ({
      ...prev,
      responsible_id: responsible.id,
    }));
    setResponsibleSearchTerm(responsible.full_name);
    setResponsiblePopoverOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast({
        title: "Erro",
        description: "O título é obrigatório",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      // Combinar data e hora
      const appointmentDateTime = new Date(`${formData.appointment_date}T${formData.appointment_time}:00`);
      
      // Buscar cor do tipo de compromisso
      const selectedType = appointmentTypes.find(type => type.value === formData.appointment_type);
      
      const appointmentData = {
        title: formData.title,
        description: formData.description,
        appointment_date: appointmentDateTime.toISOString(),
        duration: formData.duration,
        appointment_type: formData.appointment_type,
        priority: formData.priority,
        location: formData.location || null,
        prospect_name: formData.prospect_name || null,
        prospect_email: formData.prospect_email || null,
        prospect_phone: formData.prospect_phone || null,
        video_conference_link: formData.video_conference_link || null,
        is_billable: formData.is_billable,
        estimated_cost: formData.is_billable ? parseBRLStringToNumber(formData.fee_amount) || null : null,
        reminder_time: formData.push_notify ? formData.reminder_time : 0,
        reminder_offsets_minutes: formData.push_notify ? formData.reminder_offsets : [],
        reminder_sent_offsets: [] as number[],
        color_tag: selectedType?.color || '#3b82f6',
        case_id: formData.case_id,
        client_id: formData.client_id,
        responsible_id: formData.responsible_id,
        show_in_portal: !!formData.show_in_portal && !!formData.case_id,
      };

      let error;
      let savedId: string | undefined = initialData?.id;
      
      if (initialData) {
        // Edição - atualizar compromisso existente
        const { error: updateError } = await supabase
          .from('appointments')
          .update(appointmentData)
          .eq('id', initialData.id);
        error = updateError;
      } else {
        // Criação - inserir novo compromisso
        const { data: newAppointment, error: insertError } = await supabase
          .from('appointments')
          .insert({
            ...appointmentData,
            status: 'Agendado',
            user_id: (await supabase.auth.getUser()).data.user?.id,
          })
          .select()
          .single();
        error = insertError;
        savedId = newAppointment?.id;
        
        // Enviar notificação push imediata
        if (newAppointment && !insertError) {
          try {
            // Verificar se estamos em ambiente local (sem backend serverless)
            const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            
            if (isLocalhost) {
                console.info('[Push Notification] Ambiente local detectado. API ignorada para evitar erros 500.');
            } else {
                const currentUser = (await supabase.auth.getUser()).data.user;
                if (currentUser) {
                  await fetch('/api/notifications/notify-on-create', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      type: 'appointment',
                      itemId: newAppointment.id,
                      userId: currentUser.id,
                      title: formData.title,
                      appointmentDate: appointmentData.appointment_date
                    })
                  });
                }
            }
          } catch (notifError) {
            // Não bloquear criação se notificação falhar
            console.warn('Erro ao enviar notificação:', notifError);
          }
        }
      }

      if (error) throw error;

      if (savedId) await syncFeeEntry(savedId, appointmentData.appointment_date);

      requestGoogleCalendarSync();
      requestPortalSync();

      toast({
        title: "Sucesso!!",
        description: initialData ? "Compromisso atualizado com sucesso" : "Compromisso criado com sucesso"
      });

      onAppointmentCreated();
      onClose();
      
      // Reset form
      setFormData({
        title: "",
        description: "",
        appointment_date: format(selectedDate, "yyyy-MM-dd"),
        appointment_time: "09:00",
        duration: 60,
        appointment_type: "consulta",
        priority: "normal",
        location: "",
        prospect_name: "",
        prospect_email: "",
        prospect_phone: "",
        video_conference_link: "",
        is_billable: false,
    fee_amount: "",
    fee_to_financial: true,
        reminder_time: 60,
        reminder_offsets: [0, 60],
        push_notify: true,
        case_id: null,
        client_id: null,
        responsible_id: null,
        show_in_portal: false,
      });
      setCaseSearchTerm("");
      setFilteredCases([]);
      setResponsibleSearchTerm("");
      setFilteredResponsibles([]);

    } catch (error: any) {
      console.error('Erro ao salvar compromisso:', error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível salvar o compromisso",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Cobrança do compromisso -> "a receber" no Financeiro (um lançamento por compromisso)
  const syncFeeEntry = async (appointmentId: string, appointmentIso: string) => {
    const amount = parseBRLStringToNumber(formData.fee_amount);
    const wanted = formData.is_billable && formData.fee_to_financial && amount > 0;
    const { data: existing } = await (supabase as any)
      .from('financial_entries')
      .select('id, status')
      .eq('appointment_id', appointmentId)
      .maybeSingle();
    const paid = /pago/i.test(String(existing?.status || ''));

    if (!wanted) {
      if (existing && !paid) await supabase.from('financial_entries').delete().eq('id', existing.id);
      return;
    }
    if (paid) return; // já recebido: não mexe

    const dueDate = format(new Date(appointmentIso), 'yyyy-MM-dd');
    const entry = {
      description: `${formData.title} — ${format(new Date(appointmentIso), 'dd/MM/yyyy')}`,
      amount,
      due_date: dueDate,
      payment_date: dueDate,
      category: FEE_CATEGORY[formData.appointment_type] || 'Outros',
      client_id: formData.client_id,
      case_id: formData.case_id,
    };
    const { error: feeError } = existing
      ? await supabase.from('financial_entries').update(entry).eq('id', existing.id)
      : await supabase.from('financial_entries').insert({
          ...entry,
          entry_type: 'receita',
          status: 'Pendente',
          appointment_id: appointmentId,
          auto_generated: true,
          user_id: (await supabase.auth.getUser()).data.user?.id,
        } as any);
    if (feeError) {
      toast({ title: 'Compromisso salvo, mas a cobrança não foi lançada', description: feeError.message, variant: 'destructive' });
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const selectedType = appointmentTypes.find(type => type.value === formData.appointment_type);
  const selectedPriority = priorityLevels.find(priority => priority.value === formData.priority);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden bg-white shadow-2xl border border-[#c4ab83]/30">
        <DialogHeader className="relative -m-6 mb-6 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
          {/* Monograma AS como marca d'água */}
          <img
            src="/imgs/logos allana/letra_A_logo.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]"
          />
          <div className="relative flex items-center gap-4 text-left">
            <img
              src="/imgs/logos allana/letra_A_logo.png"
              alt="Allana Sthéfany"
              className="h-12 w-12 shrink-0 select-none object-contain opacity-80"
            />
            <div className="min-w-0">
              <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">
                {initialData ? 'Editar Compromisso' : 'Novo Compromisso'}
              </DialogTitle>
              <DialogDescription className="mt-1 text-sm text-slate-500">
                <span className="inline-flex items-center gap-1.5 font-medium text-[#7a6440]">
                  <Calendar className="h-3.5 w-3.5" />
                  {format(selectedDate, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                </span>
                <span className="hidden sm:inline"> · Vincule a um processo para manter tudo organizado.</span>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
          {/* Informações Básicas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Informações Básicas</h3>
              
              <div>
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Ex: Reunião com cliente, Audiência TRT..."
                  required
                  className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                />
              </div>

              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Detalhes sobre o compromisso..."
                  rows={3}
                  className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="appointment_date">Data</Label>
                  <Input
                    id="appointment_date"
                    type="date"
                    value={formData.appointment_date}
                    onChange={(e) => handleInputChange('appointment_date', e.target.value)}
                    className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                  />
                </div>
                <div>
                  <Label htmlFor="appointment_time">Horário</Label>
                  <Input
                    id="appointment_time"
                    type="time"
                    value={formData.appointment_time}
                    onChange={(e) => handleInputChange('appointment_time', e.target.value)}
                    className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="duration">Duração (minutos)</Label>
                <Select value={formData.duration.toString()} onValueChange={(value) => handleInputChange('duration', parseInt(value))}>
                  <SelectTrigger className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30">
                    <SelectValue placeholder="Selecione a duração" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 minutos</SelectItem>
                    <SelectItem value="60">1 hora</SelectItem>
                    <SelectItem value="90">1h 30min</SelectItem>
                    <SelectItem value="120">2 horas</SelectItem>
                    <SelectItem value="180">3 horas</SelectItem>
                    <SelectItem value="240">4 horas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Classificação</h3>
              
              <div>
                <Label htmlFor="appointment_type">Tipo de Compromisso</Label>
                <Select value={formData.appointment_type} onValueChange={(value) => handleInputChange('appointment_type', value)}>
                  <SelectTrigger className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {appointmentTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: type.color }}
                          />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedType && (
                  <div className="flex items-center gap-2 mt-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: selectedType.color }}
                    />
                    <span className="text-sm text-muted-foreground">
                      Cor do compromisso no calendário
                    </span>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="priority">Prioridade</Label>
                <Select value={formData.priority} onValueChange={(value) => handleInputChange('priority', value)}>
                  <SelectTrigger className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30">
                    <SelectValue placeholder="Selecione a prioridade" />
                  </SelectTrigger>
                  <SelectContent>
                    {priorityLevels.map((priority) => (
                      <SelectItem key={priority.value} value={priority.value}>
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-3 h-3" />
                          {priority.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedPriority && (
                  <Badge className={`mt-2 ${selectedPriority.color} text-white`}>
                    {selectedPriority.label}
                  </Badge>
                )}
              </div>

              {/* Toggle + chips de notificação push */}
              <div className="p-3 rounded-lg border border-primary/20 bg-primary/5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-primary" />
                    <div>
                      <p className="text-sm font-medium">Notificar por push</p>
                      <p className="text-xs text-muted-foreground">Receber alerta no celular / desktop</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.push_notify}
                    onCheckedChange={(checked) => handleInputChange('push_notify', checked)}
                  />
                </div>

                {formData.push_notify && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                      Quando lembrar (você pode escolher mais de um)
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {REMINDER_OPTIONS.map((opt) => {
                        const active = formData.reminder_offsets.includes(opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => toggleReminderOffset(opt.value)}
                            className={cn(
                              "text-xs px-3 py-1.5 rounded-full border transition-all",
                              active
                                ? "bg-primary text-primary-foreground border-primary shadow"
                                : "bg-background text-foreground border-input hover:bg-muted"
                            )}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    {formData.reminder_offsets.length === 0 && (
                      <p className="text-xs text-amber-600">
                        Nenhum lembrete selecionado — nenhum push será enviado para esse compromisso.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Detalhes do Local e Contato */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Local e Acesso
              </h3>
              
              <div>
                <Label htmlFor="location">Local</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder="Endereço, sala, tribunal..."
                  className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                />
              </div>

              <div>
                <Label htmlFor="video_conference_link">Link de Videoconferência</Label>
                <Input
                  id="video_conference_link"
                  value={formData.video_conference_link}
                  onChange={(e) => handleInputChange('video_conference_link', e.target.value)}
                  placeholder="https://meet.google.com/..."
                  className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Users className="w-4 h-4" />
                Informações de Contato
              </h3>
              
              {/* Processo Vinculado */}
              <div>
                <Label htmlFor="case_id">Processo Vinculado</Label>
                <Popover open={casePopoverOpen} onOpenChange={setCasePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={casePopoverOpen}
                      className="w-full justify-between font-normal"
                    >
                      <span className="truncate">
                        {formData.case_id
                          ? (allCases.find((caseItem) => caseItem.id === formData.case_id)?.title || caseSearchTerm || 'Processo selecionado')
                          : "Selecione um processo..."}
                      </span>
                      <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Buscar por nome, número ou cliente..."
                        value={caseSearchTerm}
                        onValueChange={setCaseSearchTerm}
                        ref={caseInputRef}
                      />
                      <CommandList>
                        <CommandEmpty>Nenhum processo encontrado.</CommandEmpty>
                        <CommandGroup>
                          {filteredCases.map((caseItem) => (
                            <CommandItem
                              key={caseItem.id}
                              value={caseItem.id}
                              onSelect={() => handleCaseSelect(caseItem)}
                            >
                              <div className="min-w-0">
                                <p className="truncate text-sm">{caseItem.title}</p>
                                <p className="truncate text-xs text-slate-400">
                                  {[caseItem.case_number_cnj || caseItem.case_number, caseItem.clients?.name].filter(Boolean).join(' · ')}
                                </p>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {formData.case_id && (
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#c4ab83]/40 bg-[#faf7f2] px-3 py-2.5">
                  <Switch
                    checked={!!formData.show_in_portal}
                    onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, show_in_portal: checked }))}
                    className="mt-0.5"
                  />
                  <span className="text-sm">
                    <span className="block font-medium text-slate-800">Mostrar ao cliente no portal</span>
                    <span className="block text-xs text-slate-500">
                      Aparece na aba "Audiências e compromissos" do cliente, com aviso. Se remarcar, atualiza; se cancelar ou excluir, sai do portal.
                    </span>
                    {initialData?.portal_published_at && (
                      <span className="mt-1 block text-xs font-medium text-emerald-700">✓ Já está no portal do cliente</span>
                    )}
                    {initialData?.portal_error && !initialData?.portal_published_at && (
                      <span className="mt-1 block text-xs text-amber-700">Não publicado: {initialData.portal_error}</span>
                    )}
                  </span>
                </label>
              )}

              {/* Responsável */}
              <div>
                <Label htmlFor="responsible_id">Responsável</Label>
                <Popover open={responsiblePopoverOpen} onOpenChange={setResponsiblePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={responsiblePopoverOpen}
                      className="w-full justify-between font-normal"
                    >
                      {formData.responsible_id
                        ? (filteredResponsibles.find((responsible) => responsible.id === formData.responsible_id)?.full_name || responsibleSearchTerm)
                        : "Selecione um responsável..."}
                      <Users className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                    <Command>
                      <CommandInput
                        placeholder="Buscar responsável..."
                        value={responsibleSearchTerm}
                        onValueChange={setResponsibleSearchTerm}
                        ref={responsibleInputRef}
                      />
                      <CommandList>
                        <CommandEmpty>Nenhum responsável encontrado.</CommandEmpty>
                        <CommandGroup>
                          {filteredResponsibles.map((responsible) => (
                            <CommandItem
                              key={responsible.id}
                              value={responsible.full_name}
                              onSelect={() => handleResponsibleSelect(responsible)}
                            >
                              {responsible.full_name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              
              <div>
                <Label>Cliente</Label>
                <Popover open={clientPopoverOpen} onOpenChange={setClientPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" aria-expanded={clientPopoverOpen} className="w-full justify-between font-normal">
                      <span className="truncate">
                        {formData.client_id
                          ? (allClients.find((c) => c.id === formData.client_id)?.name || formData.prospect_name || 'Cliente selecionado')
                          : "Selecione um cliente..."}
                      </span>
                      <Users className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                    <Command shouldFilter={false}>
                      <CommandInput placeholder="Buscar cliente..." value={clientSearchTerm} onValueChange={setClientSearchTerm} />
                      <CommandList>
                        <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
                        <CommandGroup>
                          {formData.client_id && (
                            <CommandItem value="__none" onSelect={() => handleClientSelect(null)}>
                              <span className="text-sm text-slate-500">Sem cliente (contato novo)</span>
                            </CommandItem>
                          )}
                          {filteredClients.map((client) => (
                            <CommandItem key={client.id} value={client.id} onSelect={() => handleClientSelect(client)}>
                              <div className="min-w-0">
                                <p className="truncate text-sm">{client.name}</p>
                                {(client.mobile || client.phone) && <p className="truncate text-xs text-slate-400">{client.mobile || client.phone}</p>}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {!formData.client_id && (
                <div>
                  <Label htmlFor="prospect_name">Nome do contato</Label>
                  <Input
                    id="prospect_name"
                    value={formData.prospect_name}
                    onChange={(e) => handleInputChange('prospect_name', e.target.value)}
                    placeholder="Para quem ainda não é cliente"
                    className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="prospect_email">Email</Label>
                <Input
                  id="prospect_email"
                  type="email"
                  value={formData.prospect_email}
                  onChange={(e) => handleInputChange('prospect_email', e.target.value)}
                  placeholder="email@exemplo.com"
                  className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                />
              </div>

              <div>
                <Label htmlFor="prospect_phone">Telefone</Label>
                <Input
                  id="prospect_phone"
                  value={formData.prospect_phone}
                  onChange={(e) => handleInputChange('prospect_phone', e.target.value)}
                  placeholder="(11) 99999-9999"
                  className="rounded-lg border-slate-200 bg-white shadow-none focus-visible:ring-[#c4ab83]/30"
                />
              </div>
            </div>
          </div>

          {/* Cobrança */}
          <div className="rounded-xl border border-[#c4ab83]/40 bg-[#faf7f2] p-4">
            <label className="flex cursor-pointer items-center justify-between gap-3">
              <span className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-[#a88a5c] ring-1 ring-[#c4ab83]/30">
                  <Wallet className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-sm font-medium text-slate-800">Cobrar por este compromisso</span>
                  <span className="block text-xs text-slate-500">Diligência, audiência avulsa, consulta…</span>
                </span>
              </span>
              <Switch
                checked={formData.is_billable}
                onCheckedChange={(checked) => handleInputChange('is_billable', checked)}
              />
            </label>

            {formData.is_billable && (
              <div className="mt-4 grid gap-4 border-t border-[#c4ab83]/25 pt-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="fee_amount" className="text-sm text-slate-700">Valor</Label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">R$</span>
                    <Input
                      id="fee_amount"
                      inputMode="numeric"
                      value={formData.fee_amount}
                      onChange={(e) => handleInputChange('fee_amount', formatBRLFromDigits(e.target.value))}
                      placeholder="0,00"
                      className="rounded-lg border-slate-200 bg-white pl-9 text-right font-medium tabular-nums shadow-none focus-visible:ring-[#c4ab83]/30"
                    />
                  </div>
                </div>
                <label className="flex cursor-pointer items-start gap-2.5 self-end rounded-lg bg-white px-3 py-2.5 ring-1 ring-slate-200">
                  <Switch
                    checked={formData.fee_to_financial}
                    onCheckedChange={(checked) => handleInputChange('fee_to_financial', checked)}
                    className="mt-0.5"
                  />
                  <span className="text-sm">
                    <span className="block font-medium text-slate-800">Lançar no Financeiro</span>
                    <span className="block text-xs text-slate-500">
                      Entra como "a receber"{formData.client_id ? ' do cliente' : ''}, com vencimento na data do compromisso.
                    </span>
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* Botões de Ação */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="flex items-center gap-2 bg-[#c4ab83] font-semibold text-slate-900 hover:bg-[#b39a72]"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {initialData ? "Atualizando..." : "Criando..."}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  {initialData ? "Atualizar Compromisso" : "Criar Compromisso"}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};